<script setup>
  import { ref, onMounted } from 'vue';
  import { useI18n } from 'vue-i18n';
  import axios from '../../services/ApiService.js';

  const { t } = useI18n();

  const props = defineProps({
    recentPath: { type: String, required: true }
  });

  const isLoading = ref(false);
  const imprints = ref([]);

  const publisherUrl = (id) => `${props.recentPath}?imprint_ids=${id}&languages=all&release_date=recent_month&ignore_preferences=true`;
  const truncate = (str, n) => str?.length > n ? str.slice(0, n) + '…' : str;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  onMounted(async () => {
    isLoading.value = true;
    try {
      const response = await axios.get('/api/imprints/followed_recent_publications.json');
      imprints.value = response.data.imprints || [];
    } catch {
      imprints.value = [];
    } finally {
      isLoading.value = false;
    }
  });
</script>

<template>
  <div>
    <div v-if="isLoading" class="text-center py-4">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">{{ t('components.imprints_list.loading') }}</span>
      </div>
    </div>

    <template v-else>
      <p class="text-muted small mb-4">{{ t('components.imprints_list.recent_publications_note') }}</p>

      <div v-for="item in imprints" :key="item.imprint.id" class="mb-5">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5 class="mb-0">
            <a :href="publisherUrl(item.imprint.id)" target="_blank" rel="noopener noreferrer">
              {{ item.imprint.name }}
            </a>
          </h5>
          <a
            :href="publisherUrl(item.imprint.id)"
            target="_blank"
            rel="noopener noreferrer"
            class="btn mkt-btn-outline-primary btn-sm"
          >
            {{ t('components.imprints_list.show_all') }}
          </a>
        </div>

        <div class="block__scroll-wrapper">
          <ul class="block__items hide-scrollbar">
            <li v-for="pub in item.publications" :key="pub.id" class="favorite-publishers__card">
              <a :href="`/item/${pub.id}`" target="_blank" rel="noopener noreferrer" class="text-decoration-none">
                <img
                  v-if="pub.cover"
                  :src="pub.cover"
                  :alt="pub.title"
                  class="favorite-publishers__cover"
                />
                <div class="favorite-publishers__card-body">
                  <div class="favorite-publishers__title" :title="pub.title">{{ truncate(pub.title, 40) }}</div>
                  <div class="favorite-publishers__authors text-muted small">{{ pub.authors.join(', ') }}</div>
                  <div class="favorite-publishers__date text-muted small">{{ formatDate(pub.publication_date) }}</div>
                </div>
              </a>
            </li>
          </ul>
          <button class="block__arrow block__arrow--prev block__arrow--hidden" aria-hidden="true" tabindex="-1">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
              <path fill-rule="evenodd" d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"/>
            </svg>
          </button>
          <button class="block__arrow block__arrow--next" aria-hidden="true" tabindex="-1">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
              <path fill-rule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"/>
            </svg>
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
  .block__items {
    padding-left: 0;
    padding-top: 0;
  }

  .favorite-publishers__card {
    flex: 0 0 130px;
    margin-right: 1rem;

    a {
      display: block;
      color: inherit;
    }
  }

  .favorite-publishers__cover {
    width: 130px;
    height: 185px;
    object-fit: cover;
    border-radius: 4px;
    display: block;
  }

  .favorite-publishers__card-body {
    padding: 0.4rem 0;
  }

  .favorite-publishers__title {
    font-size: 0.8rem;
    font-weight: 600;
    line-height: 1.3;
    margin-bottom: 0.15rem;
  }

  .favorite-publishers__authors,
  .favorite-publishers__date {
    font-size: 0.75rem;
    line-height: 1.2;
  }
</style>
